import { Button, IconButton, Spinner } from '@material-tailwind/react';
import { EyeIcon, PlusIcon, TrashIcon, UsersIcon } from 'lucide-react';
import React, { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { useGetTeamMembers, useUpdateUser } from '../../../custom-hooks/use-users';
import { useConfirm } from '../../../custom-hooks/confirm-dialog';
import CustomerFormModal from '../customerFormModal';
import { STATIC_USER_ROLES } from '../../../utils/enums';
import { Notify } from '../../../utils/functions';
import { usePermissions } from '../../../custom-hooks/use-permissions';

const INITIAL_TEAM_MEMBER_FORM_DATA = {
    name: '',
    email: '',
    phone: '',
    cnic: '',
    role: STATIC_USER_ROLES.TEAM_MEMBER,
    teamId: '',
};

const TeamMembers = ({ customer }) => {

    const [isAddTeamMemberModalOpen, setIsAddTeamMemberModalOpen] = useState(false);

    const { can } = usePermissions();
    const canEdit = can('FX12-1-1', 'edit');

    const {
        data: teamMembers,
        isLoading: isTeamMembersLoading,
        error: teamMembersError,
        refetch: refetchTeamMembers,
    } = useGetTeamMembers(customer._id);

    const [isEditingLimit, setIsEditingLimit] = useState(false);
    const [maxTeamMembers, setMaxTeamMembers] = useState(customer?.customerProfile?.maxTeamMembers || 5);
    const updateUserMutation = useUpdateUser();

    const handleSaveLimit = async () => {
        try {
            await updateUserMutation.mutateAsync({
                userId: customer._id,
                data: { maxTeamMembers }
            });
            setIsEditingLimit(false);
        } catch (error) {
            console.error("Failed to update limit", error);
        }
    };

    const { confirm, ConfirmationDialog } = useConfirm();

    const handleAddTeamMember = () => {
        setIsAddTeamMemberModalOpen(true);
    }

    const handleDeleteTeamMember = async (userId) => {
        const confirmation = await confirm({
            title: "Delete Team Member",
            message: "Are you sure you want to delete this team member?",
            confirmText: "Delete",
            cancelText: "Cancel",
            confirmBtnColor: "red",
        });
        if (confirmation) {
            // deleteTeamMemberMutation.mutate(userId);
        }
    }

    return (
        <Fragment>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border rounded-xl p-6">
                    <div className="flex justify-between items-center gap-4">
                        <h4 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                            <UsersIcon size={16} />
                            Manage Team Members
                        </h4>
                        <div className="flex items-center gap-2">
                            {canEdit && (
                                <Fragment>
                                    {!isEditingLimit ? (
                                        <div className="flex items-center gap-2 mr-4">
                                            <span className="text-sm text-gray-600">Limit: {customer?.customerProfile?.maxTeamMembers || 5}</span>
                                            <Button variant="text" size="sm" onClick={() => setIsEditingLimit(true)} className="p-1">Edit</Button>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-2 mr-4">
                                            <input 
                                                type="number" 
                                                className="border rounded px-2 py-1 w-20 text-sm" 
                                                value={maxTeamMembers} 
                                                onChange={(e) => setMaxTeamMembers(parseInt(e.target.value) || 0)}
                                            />
                                            <Button variant="gradient" size="sm" className="p-2" onClick={handleSaveLimit} loading={updateUserMutation.isLoading}>Save</Button>
                                            <Button variant="text" size="sm" className="p-2" onClick={() => setIsEditingLimit(false)}>Cancel</Button>
                                        </div>
                                    )}
                                    <Button variant="gradient" size="sm" className="flex items-center gap-2 rounded-full" onClick={handleAddTeamMember}>
                                        <PlusIcon size={12} />
                                        Add Team Member
                                    </Button>
                                </Fragment>
                            )}
                        </div>
                    </div>
                    <div className="mt-4 flex flex-col gap-2">
                        {
                            isTeamMembersLoading ? <Spinner /> : teamMembersError ? <div className="text-center py-8 text-red-500">Error loading team members</div> :
                                teamMembers?.users?.length === 0 ? <div className="text-center py-8 text-gray-500">No team members found</div> :
                                    teamMembers?.users.map((member) => (
                                        <div key={member._id} className="flex justify-between items-center gap-2 border rounded-xl p-4">
                                            <div className="flex items-center gap-2">
                                                <p className="text-md text-gray-600">{member.name}</p>
                                                <p className="text-xs text-gray-500">{member.email}</p>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <Link to={`/customer/${customer._id}/team-member/${member._id}`} className="hover:text-orange-900 duration-300">
                                                    <IconButton
                                                        size="sm"
                                                        className="rounded-full"
                                                        color="blue"
                                                    >
                                                        <EyeIcon size={12} />
                                                    </IconButton>
                                                </Link>
                                                {canEdit && (
                                                    <IconButton variant="gradient" color="red" size="sm" className="rounded-full hidden" onClick={() => handleDeleteTeamMember(member._id)}>
                                                        <TrashIcon size={12} />
                                                    </IconButton>
                                                )}
                                            </div>
                                        </div>
                                    ))
                        }
                    </div>
                </div>
            </div>
            <CustomerFormModal //To add team member under a customer
                isOpen={isAddTeamMemberModalOpen}
                onClose={() => setIsAddTeamMemberModalOpen(false)}
                defaultData={{ ...INITIAL_TEAM_MEMBER_FORM_DATA, teamId: customer._id }}
                isUpdating={false}
                updatingUserId={customer._id}
                callback={() => refetchTeamMembers()}
            />
            <ConfirmationDialog />
        </Fragment>
    );
};

export default TeamMembers;